import React, { useState } from 'react';
import { whatsappAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  X, MessageCircle, Loader2, Copy, CheckCircle, ExternalLink, Send
} from 'lucide-react';
import { toast } from 'sonner';

const WhatsAppShareModal = ({ isOpen, onClose, cv }) => {
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [whatsappUrl, setWhatsappUrl] = useState('');
  const [copied, setCopied] = useState(false);
  
  const [formData, setFormData] = useState({
    job_title: '',
    company_name: '',
    contact_number: cv?.personal_details?.phone || ''
  });

  const handleGenerate = async () => {
    if (!formData.job_title.trim() || !formData.company_name.trim()) {
      toast.error('Please fill in the job title and company name');
      return;
    }

    setLoading(true);
    try {
      const response = await whatsappAPI.generateMessage({
        cv_id: cv.id,
        job_title: formData.job_title,
        company_name: formData.company_name,
        contact_number: formData.contact_number
      });
      setMessage(response.data.message);
      setWhatsappUrl(response.data.whatsapp_url);
      toast.success('Message generated!');
    } catch (error) {
      toast.error('Failed to generate message');
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(message);
    setCopied(true);
    toast.success('Message copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenWhatsApp = () => {
    window.open(whatsappUrl, '_blank');
  };

  const handleReset = () => {
    setMessage('');
    setWhatsappUrl('');
    setFormData({
      job_title: '',
      company_name: '',
      contact_number: cv?.personal_details?.phone || ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
              <MessageCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">Share via WhatsApp</h2>
              <p className="text-xs text-slate-500">Generate a professional message</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
          >
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        {/* Content */}
        <div className="p-5">
          {!message ? (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Enter the job details to generate a professional WhatsApp message for your application.
              </p>

              <div>
                <Label className="text-slate-700">Job Title *</Label>
                <Input
                  value={formData.job_title}
                  onChange={(e) => setFormData(prev => ({ ...prev, job_title: e.target.value }))}
                  placeholder="e.g., Sales Representative"
                  className="mt-1.5 h-11"
                />
              </div>

              <div>
                <Label className="text-slate-700">Company Name *</Label>
                <Input
                  value={formData.company_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, company_name: e.target.value }))}
                  placeholder="e.g., ABC Company"
                  className="mt-1.5 h-11"
                />
              </div>

              <div>
                <Label className="text-slate-700">Your Contact Number</Label>
                <Input
                  value={formData.contact_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, contact_number: e.target.value }))}
                  placeholder="e.g., 082 123 4567"
                  className="mt-1.5 h-11"
                />
              </div>

              <Button
                onClick={handleGenerate}
                disabled={loading}
                className="w-full h-11 bg-green-600 hover:bg-green-700 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Generate Message
                  </>
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-green-800">Your Message</span>
                  <button
                    onClick={handleCopy}
                    className="text-green-600 hover:text-green-700 flex items-center gap-1 text-sm"
                  >
                    {copied ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                    {copied ? 'Copied!' : 'Copy'}
                  </button>
                </div>
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="min-h-[200px] bg-white border-green-200 text-sm"
                />
                <p className="text-xs text-green-600 mt-2">
                  Feel free to edit the message before sending
                </p>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={handleOpenWhatsApp}
                  className="flex-1 h-11 bg-green-600 hover:bg-green-700 text-white"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Open WhatsApp
                  <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
                <Button
                  variant="outline"
                  onClick={handleReset}
                  className="h-11"
                >
                  New Message
                </Button>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-xs text-amber-800">
                  <strong>Remember:</strong> Attach your CV PDF after pasting the message in WhatsApp!
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WhatsAppShareModal;
